import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Icons } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTree, useTreePeople } from '../contexts/TreeContext';
import { getInitials, byName } from '../utils/helpers';
import { auth } from '../utils/firebase';
import { usePersonPhotoUrl } from '../utils/signedUrls';
import { formatBytes } from './AdminDashboard';

const ITEM_HEIGHT = 50;
const OVERSCAN = 4;

const ROLE_STYLE = {
  owner:       { bg: 'rgba(196,151,90,0.25)',  color: '#C4975A' },
  admin:       { bg: 'rgba(109,174,114,0.25)', color: '#6DAE72' },
  contributor: { bg: 'rgba(91,138,196,0.25)',  color: '#5B8AC4' },
  viewer:      { bg: 'rgba(160,147,127,0.25)', color: '#A0937F' },
};

function RoleBadge({ role }) {
  const s = ROLE_STYLE[role] || ROLE_STYLE.viewer;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: s.bg, color: s.color,
      textTransform: 'uppercase', letterSpacing: 0.6, flexShrink: 0,
    }}>
      {role}
    </span>
  );
}

export default function Sidebar({ searchQuery, setSearchQuery, onSelectPerson, selectedId, onAddPerson, onManageAccess, onUpgradeClick, onManageSubscription, onDeleteAccount, onCookieSettings, canEdit, isMobile, isAdmin, onShowAdmin }) {
  const { user, logout } = useAuth();
  const { userRole, userTrees, activeTreeId, switchTree, renameTree, createTree, peopleCount, storageBytes } = useTree();
  const { people } = useTreePeople();
  const { tier, maxPeople, maxStorageBytes } = useSubscription();

  const peoplePct = maxPeople ? peopleCount / maxPeople : 0;
  const storagePct = maxStorageBytes ? storageBytes / maxStorageBytes : 0;
  const usageColor = (pct) => (pct >= 1 ? '#E07B6F' : pct >= 0.8 ? '#C4975A' : '#8B7D6B');

  const [showTreeMenu, setShowTreeMenu] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  // Closing the input (below) fires a native blur regardless of why it closed —
  // this tracks whether that blur should be skipped: 'cancel' (Escape already
  // handled the close) or 'saving' (Enter already triggered a save).
  const nameCloseReasonRef = useRef(null); // 'cancel' | 'saving' | null
  const [nudgeDismissed, setNudgeDismissed] = useState(true); // start hidden until checked
  const [creatingTree, setCreatingTree] = useState(false);
  const [createTreeError, setCreateTreeError] = useState('');

  // Creates a tree of the user's own and switches to it. The name is left to
  // defaultTreeName rather than prompted for: this sits in a dropdown, and
  // stopping to ask for a name is the kind of friction that loses the person who
  // was only half-decided. The owner can rename it in place from the same header
  // the moment it opens.
  const handleCreateTree = async () => {
    if (creatingTree) return;
    setCreatingTree(true);
    setCreateTreeError('');
    try {
      await createTree();
      setShowTreeMenu(false);
    } catch (e) {
      // Kept in the menu rather than thrown away — the per-account cap is a
      // real answer the user needs to read, not a transient failure to retry.
      setCreateTreeError(e?.message || 'Could not create the tree. Please try again.');
    } finally {
      setCreatingTree(false);
    }
  };

  // Force-refreshes the ID token first: email_verified in the cached JWT can
  // be stale relative to the account's actual state (see TreeWizard.js), and
  // a stale claim makes firestore.rules' canWrite silently reject the write
  // with no error surfaced, which looks exactly like "the rename reverted".
  const handleRenameSave = async () => {
    if (nameCloseReasonRef.current != null) { nameCloseReasonRef.current = null; return; }
    nameCloseReasonRef.current = 'saving';
    setIsEditingName(false);
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== activeTree?.name) {
      try {
        if (auth.currentUser) await auth.currentUser.getIdToken(true);
        await renameTree(trimmed);
      } catch (e) {
        window.alert(e?.message || 'Could not rename the tree. Please try again.');
      }
    }
    nameCloseReasonRef.current = null; // safety net if the blur-triggered consume above never fired
  };

  const nudgeKey = user ? `kinship_nudge_dismissed_${user.uid}` : null;

  // Check if nudge should show: viewing a non-owned tree + not dismissed within 7 days
  useEffect(() => {
    if (!nudgeKey || userRole === null) return;
    if (userRole === 'owner') { setNudgeDismissed(true); return; }
    // Deliberately not gated on already owning a tree. It used to be, which
    // inverted the panel's own copy: "start your own" was shown only to people
    // who already had one (where it acts as a shortcut back to it) and hidden
    // from everyone who did not — the invited user it is written for.
    const ts = localStorage.getItem(nudgeKey);
    if (ts && Date.now() - Number(ts) < 7 * 24 * 60 * 60 * 1000) {
      setNudgeDismissed(true);
    } else {
      setNudgeDismissed(false);
    }
  }, [nudgeKey, userRole, userTrees]);

  const dismissNudge = () => {
    localStorage.setItem(nudgeKey, String(Date.now()));
    setNudgeDismissed(true);
  };

  const activeTree = userTrees.find(t => t.treeId === activeTreeId);
  // One owned tree per account. Someone can be viewer, contributor or admin on
  // any number of other people's trees, but the tree that is *theirs* is
  // singular — so the offer to start one disappears once it exists rather than
  // sitting there inviting a second. The server holds the same rule; this only
  // keeps the UI from offering what it would refuse.
  const ownedTree = userTrees.find(t => t.role === 'owner');

  const filtered = useMemo(() =>
    Object.values(people)
      .filter(p => (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
      .sort(byName),
    [people, searchQuery]
  );

  const [scrollTop, setScrollTop] = useState(0);
  const listRef = useRef(null);

  const viewportHeight = listRef.current?.clientHeight ?? 400;
  const startIdx = Math.max(0, Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN);
  const endIdx = Math.min(filtered.length, Math.ceil((scrollTop + viewportHeight) / ITEM_HEIGHT) + OVERSCAN);
  const paddingTop = startIdx * ITEM_HEIGHT;
  const paddingBottom = Math.max(0, (filtered.length - endIdx) * ITEM_HEIGHT);
  const visiblePeople = filtered.slice(startIdx, endIdx);

  return (
    <div style={{
      width: isMobile ? '100%' : 280, background: '#2C2418', color: '#F5EDE3',
      display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif",
      overflowY: 'auto',
      ...(isMobile ? { flex: 1, minHeight: 0 } : { height: '100%' }),
    }}>
      {/* Logo */}
      <div style={{ padding: '22px 20px 14px', borderBottom: '1px solid rgba(196,151,90,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/kt_logo.png"
            alt="Kinship Trees"
            style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain', flexShrink: 0 }}
          />
          <div>
            <h1 style={{ margin: 0, fontSize: 17, fontFamily: "'Crimson Pro', Georgia, serif", fontWeight: 700, letterSpacing: 0.3 }}>Kinship Trees</h1>
            <p style={{ margin: 0, fontSize: 10, color: '#A0937F', letterSpacing: 1.5, textTransform: 'uppercase' }}>Family History</p>
          </div>
        </div>

        {/* Tree switcher */}
        <div style={{ position: 'relative', marginTop: 14 }}>
          {isEditingName ? (
            <div style={{
              width: '100%', background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(196,151,90,0.5)', borderRadius: 8,
              padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8,
              boxSizing: 'border-box',
            }}>
              <Icons.Tree size={13} />
              <input
                autoFocus
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleRenameSave();
                  if (e.key === 'Escape') { nameCloseReasonRef.current = 'cancel'; setIsEditingName(false); }
                }}
                onBlur={handleRenameSave}
                style={{
                  flex: 1, background: 'none', border: 'none', outline: 'none',
                  color: '#F5EDE3', fontSize: 12, fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              />
            </div>
          ) : (
          <button
            onClick={() => setShowTreeMenu(v => !v)}
            style={{
              width: '100%', background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(196,151,90,0.25)', borderRadius: 8,
              padding: '8px 10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              color: '#F5EDE3', fontFamily: "'DM Sans', sans-serif",
            }}
          >
            <Icons.Tree size={13} />
            <span style={{
              flex: 1, textAlign: 'left', fontSize: 12, fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {activeTree?.name || 'Select tree'}
            </span>
            <RoleBadge role={userRole} />
            {userRole === 'owner' && (
              <span
                title="Rename tree"
                onClick={e => {
                  e.stopPropagation();
                  setDraftName(activeTree?.name || '');
                  setIsEditingName(true);
                }}
                style={{ color: '#8B7D6B', display: 'flex', flexShrink: 0, padding: 2 }}
              >
                <Icons.Edit size={11} />
              </span>
            )}
            <Icons.ChevronDown size={12} />
          </button>
          )}

          {showTreeMenu && (
            <>
              {/* Backdrop to close menu */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 150 }}
                onClick={() => setShowTreeMenu(false)}
              />
              <div style={{
                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                zIndex: 200, background: '#3A3028', borderRadius: 8,
                border: '1px solid rgba(196,151,90,0.25)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden',
              }}>
                {userTrees.map(t => (
                  <button
                    key={t.treeId}
                    onClick={() => { switchTree(t.treeId); setShowTreeMenu(false); }}
                    style={{
                      width: '100%', padding: '10px 14px',
                      background: t.treeId === activeTreeId ? 'rgba(196,151,90,0.15)' : 'none',
                      border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8,
                      color: '#F5EDE3', fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <span style={{
                      flex: 1, textAlign: 'left', fontSize: 13,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {t.name}
                    </span>
                    <RoleBadge role={t.role} />
                    {t.treeId === activeTreeId && (
                      <span style={{ color: '#C4975A', flexShrink: 0 }}><Icons.Check size={12} /></span>
                    )}
                  </button>
                ))}

                {!ownedTree && (<>
                {/* Start a tree of your own.
                    Bootstrap only mints a starter tree for an account whose
                    list is empty, so anyone who arrived through an invite link
                    has none and no way to ask for one — the switcher was a
                    dead end for exactly the person most likely to want their
                    own side of the family. */}
                <button
                  onClick={handleCreateTree}
                  disabled={creatingTree}
                  style={{
                    width: '100%', padding: '10px 14px',
                    background: 'none', border: 'none',
                    borderTop: '1px solid rgba(196,151,90,0.25)',
                    cursor: creatingTree ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 8,
                    color: '#C4975A', fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13, fontWeight: 600, textAlign: 'left',
                  }}
                >
                  <Icons.Plus size={13} />
                  <span style={{ flex: 1 }}>
                    {creatingTree ? 'Creating…' : 'Create my own tree'}
                  </span>
                </button>

                {createTreeError && (
                  <p style={{
                    margin: 0, padding: '0 14px 10px',
                    fontSize: 11, color: '#E07B6F', lineHeight: 1.5,
                  }}>
                    {createTreeError}
                  </p>
                )}
                </>)}
              </div>
            </>
          )}
        </div>

        {/* Own-tree nudge — shown when viewing a shared tree */}
        {!nudgeDismissed && (() => {
          return (
            <div style={{
              marginTop: 10,
              background: 'rgba(196,151,90,0.10)',
              border: '1px solid rgba(196,151,90,0.3)',
              borderRadius: 8, padding: '10px 12px',
              position: 'relative',
            }}>
              <button
                onClick={dismissNudge}
                title="Dismiss"
                style={{
                  position: 'absolute', top: 6, right: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#8B7D6B', fontSize: 14, lineHeight: 1, padding: 0,
                }}
              >×</button>
              <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#C4975A' }}>
                Your tree is waiting
              </p>
              <p style={{ margin: '0 0 8px', fontSize: 11, color: '#A0937F', lineHeight: 1.5 }}>
                You're viewing a shared tree. Start your own to capture your other side of the family — then share it back.
              </p>
              {/* Two different actions behind one nudge. With an owned tree
                  this only ever switched to it, which is not "starting" one —
                  and without an owned tree the button did not render at all, so
                  an invited user read "start your own" above a panel that
                  offered no way to. */}
              <button
                onClick={ownedTree
                  ? () => { switchTree(ownedTree.treeId); dismissNudge(); }
                  : handleCreateTree}
                disabled={creatingTree}
                style={{
                  background: 'rgba(196,151,90,0.2)', border: '1px solid rgba(196,151,90,0.4)',
                  borderRadius: 6, padding: '5px 10px',
                  cursor: creatingTree ? 'wait' : 'pointer',
                  color: '#C4975A', fontSize: 11, fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {ownedTree ? 'Go to My Tree →' : creatingTree ? 'Creating…' : 'Start My Tree →'}
              </button>
            </div>
          );
        })()}
      </div>

      {/* Search */}
      <div style={{ padding: '16px 16px 8px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(255,255,255,0.06)', borderRadius: 8,
          padding: '8px 12px', border: '1px solid rgba(196,151,90,0.15)',
        }}>
          <Icons.Search />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search people..."
            style={{
              background: 'none', border: 'none', outline: 'none',
              color: '#F5EDE3', fontSize: 13, width: '100%',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>
      </div>

      {/* Nav — desktop only. On the phone these rows do nothing when tapped
          (the bottom tab bar is the navigation there), and the height they
          take comes straight out of the people list below. */}
      {!isMobile && <div style={{ padding: '8px 12px' }}>
        <NavItem icon={<Icons.Home />} label="Tree View" active />
        <NavItem icon={<Icons.Person />} label="All People" badge={Object.keys(people).length} />
        {/* Admin's way into the dashboard. It used to be a floating button
            pinned to the bottom-right of the viewport, which is where the
            canvas keeps its zoom and export tools — so it sat on top of them.
            No fixed corner is safe for it either: those tools live inside the
            canvas and slide left whenever the detail panel opens. The sidebar
            is 280px wide and never moves, and this is where the mobile shell
            already puts the same link. */}
        {isAdmin && (
          <NavItem icon={<Icons.Edit />} label="Admin Dashboard" onClick={onShowAdmin} />
        )}
      </div>}

      {!isMobile && <div style={{ height: 1, background: 'rgba(196,151,90,0.15)', margin: '4px 16px' }} />}

      {/* People list. It is the only section that shrinks, so without a floor
          a short screen (an iPhone in Safari) squeezes it to zero height and
          search looks broken: the matches are there, just 0px tall. The floor
          keeps a few rows visible; the sidebar scrolls if it has to. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 3 * ITEM_HEIGHT + 40, padding: '8px 12px 0' }}>
        {/* Pinned header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '4px 8px', marginBottom: 6, flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, color: '#A0937F', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700 }}>
            Family Members
          </span>
          {canEdit && (
            <button onClick={onAddPerson} style={{
              background: 'rgba(196,151,90,0.15)', border: 'none',
              borderRadius: 6, padding: '4px 6px', cursor: 'pointer',
              color: '#C4975A', display: 'flex', alignItems: 'center',
            }}><Icons.Plus /></button>
          )}
        </div>

        {/* Virtual scroll container */}
        <div
          ref={listRef}
          onScroll={e => setScrollTop(e.currentTarget.scrollTop)}
          style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}
        >
          {filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#8B7D6B', fontSize: 12, marginTop: 20 }}>
              {Object.keys(people).length === 0 ? 'No people yet' : 'No results'}
            </p>
          ) : (
            <>
              <div style={{ height: paddingTop }} />
              {visiblePeople.map(p => (
                <PersonListItem key={p.id} p={p} isSelected={selectedId === p.id}
                  onSelect={onSelectPerson} />
              ))}
              <div style={{ height: paddingBottom }} />
            </>
          )}
        </div>
      </div>

      {/* Add Person / Manage Subscription / Manage Access — the two secondary
          (outline-style) buttons share one icon size, font size/weight, and
          padding so they read as a matched pair; Add Person stays the sole
          bold gradient CTA. */}
      <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(196,151,90,0.15)', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {canEdit && (
          <button onClick={onAddPerson} style={{
            width: '100%', padding: '11px 0', borderRadius: 8,
            background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
            border: 'none', color: '#2C2418', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Icons.Plus /> Add Person
          </button>
        )}
        {/* Desktop only — mobile already covers this via the "Me" tab (App.js MeTab) */}
        {!isMobile && (tier === 'paid' ? (
          <button onClick={onManageSubscription} style={{
            width: '100%', padding: '9px 0', borderRadius: 8,
            background: 'rgba(107,94,79,0.15)',
            border: '1px solid rgba(107,94,79,0.3)', color: '#A0937F', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Icons.Star /> Manage Subscription
          </button>
        ) : (
          <button onClick={onUpgradeClick} style={{
            width: '100%', padding: '11px 0', borderRadius: 8,
            background: 'linear-gradient(135deg, #C4975A 0%, #A07840 100%)',
            border: 'none', color: '#2C2418', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Icons.Star /> Upgrade to Paid
          </button>
        ))}
        {userRole && (
          <button onClick={onManageAccess} style={{
            width: '100%', padding: '9px 0', borderRadius: 8,
            background: 'rgba(196,151,90,0.12)',
            border: '1px solid rgba(196,151,90,0.25)', color: '#C4975A', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <Icons.Users /> {userRole === 'owner' ? 'Manage Access' : 'Invite Someone'}
          </button>
        )}
      </div>

      {/* Tree usage against plan limits — unobtrusive, always visible so
          limits are never a surprise (vs. only finding out via the quota
          banner or a blocked action). */}
      <div style={{
        padding: '8px 16px', borderTop: '1px solid rgba(196,151,90,0.15)',
        display: 'flex', justifyContent: 'space-between', gap: 10,
      }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: usageColor(peoplePct) }}>
          {peopleCount} / {maxPeople} people
        </span>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: usageColor(storagePct) }}>
          {formatBytes(storageBytes)} / {formatBytes(maxStorageBytes)}
        </span>
      </div>

      {/* User profile / logout — desktop only; the Me tab carries both on the phone. */}
      {!isMobile && <div style={{
        padding: '12px 16px', borderTop: '1px solid rgba(196,151,90,0.15)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'rgba(196,151,90,0.2)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, color: '#C4975A',
          overflow: 'hidden',
        }}>
          {user?.photoURL
            ? <img src={user.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : getInitials(user?.displayName || user?.email || '?')
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: '#F5EDE3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.displayName || 'User'}
          </p>
          <p style={{ margin: 0, fontSize: 10, color: '#8B7D6B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.email || ''}
          </p>
        </div>
        <button onClick={logout} title="Sign out" style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#8B7D6B', padding: 4,
        }}>
          <Icons.Logout />
        </button>
      </div>}

      {/* Support */}
      <div style={{ padding: '8px 16px 12px', textAlign: 'center' }}>
        <a
          href="mailto:support@kinshiptrees.com"
          style={{ fontSize: 10, color: '#6B5E4F', textDecoration: 'none' }}
        >
          Need help? support@kinshiptrees.com
        </a>
        {/* Findable by anyone looking for it, and impossible to hit by accident.
            Everything that makes deletion safe is in the dialog, not in how
            hard this is to reach. Desktop only, with Cookie settings below: the
            phone gets no handlers for either, and the Me tab has both. */}
        {!isMobile && <button onClick={onDeleteAccount} style={{
          display: 'block', width: '100%', marginTop: 8, padding: 0,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 10, color: '#6B5E4F', textDecoration: 'underline',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          Delete my account
        </button>}
        {/* The privacy policy promises cookie settings on every page. */}
        {!isMobile && <button onClick={onCookieSettings} style={{
          display: 'block', width: '100%', marginTop: 8, padding: 0,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 10, color: '#6B5E4F', textDecoration: 'underline',
          fontFamily: "'DM Sans', sans-serif",
        }}>
          Cookie settings
        </button>}
      </div>
    </div>
  );
}

function PersonListItem({ p, isSelected, onSelect }) {
  const photoUrl = usePersonPhotoUrl(p, 32);
  return (
    <div
      onClick={() => onSelect(p.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
        height: ITEM_HEIGHT, boxSizing: 'border-box',
        background: isSelected ? 'rgba(196,151,90,0.15)' : 'transparent',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: p.gender === 'F' ? 'rgba(219,200,206,0.35)' : 'rgba(200,206,219,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: '#C4975A',
        fontFamily: "'Crimson Pro', Georgia, serif",
        overflow: 'hidden', flexShrink: 0,
      }}>
        {photoUrl
          ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : getInitials(p.name)
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 13, fontWeight: 500, color: '#F5EDE3',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{p.name}</p>
        <p style={{ margin: 0, fontSize: 11, color: '#8B7D6B' }}>
          {p.birth?.slice(0, 4) || '?'}–{p.death ? p.death.slice(0, 4) : 'present'}
        </p>
      </div>
      {!p.death && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#6DAE72', flexShrink: 0 }} />}
    </div>
  );
}

function NavItem({ icon, label, active, badge, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
      background: active ? 'rgba(196,151,90,0.12)' : 'transparent',
      color: active ? '#C4975A' : '#A0937F',
      marginBottom: 2,
    }}>
      {icon}
      <span style={{ fontSize: 13, fontWeight: active ? 600 : 500 }}>{label}</span>
      {badge !== undefined && (
        <span style={{
          marginLeft: 'auto', fontSize: 11, fontWeight: 700,
          background: 'rgba(196,151,90,0.15)', padding: '2px 8px',
          borderRadius: 10, color: '#C4975A',
        }}>{badge}</span>
      )}
    </div>
  );
}
